/**
 * Timezone-aware date utilities. The active timezone is configurable
 * per tenant via org_settings.timezone. BrandingProvider calls setTimezone()
 * on load so this module reflects the current account's timezone.
 *
 * Default: America/Vancouver (for backwards compatibility with existing data
 * and pre-load renders before branding resolves).
 *
 * Locale-aware date formatting. The active locale is configurable per tenant
 * via account country (org_settings.country). BrandingProvider calls setLocale()
 * on load so this module reflects the current account's locale.
 *
 * Default: 'en-CA' (for backwards compatibility with existing data
 * and pre-load renders before branding resolves).
 */

let TZ = 'America/Vancouver'
let LOCALE = 'en-CA'

/**
 * Set the active timezone. Called by BrandingProvider after loading org_settings.
 * @param {string} tz - IANA timezone identifier
 * @returns {void}
 */
export function setTimezone(tz) {
  if (tz && typeof tz === 'string') TZ = tz
}

/**
 * Get the current active timezone.
 * @returns {string}
 */
export function getTimezone() {
  return TZ
}

/**
 * Set the active locale. Called by BrandingProvider after loading org_settings.country.
 * @param {string} locale - BCP 47 language tag (e.g. 'en-CA', 'en-US')
 * @returns {void}
 */
export function setLocale(locale) {
  if (locale && typeof locale === 'string') LOCALE = locale
}

/**
 * Get the current active locale.
 * @returns {string}
 */
export function getLocale() {
  return LOCALE
}

/**
 * Returns today's date as 'YYYY-MM-DD' in the active timezone.
 * @returns {string}
 */
export function accountToday() {
  return toAccountDate(new Date())
}

/**
 * Converts any Date to 'YYYY-MM-DD' in the active timezone and locale.
 * @param {Date} date
 * @returns {string}
 */
export function toAccountDate(date) {
  return date.toLocaleDateString(getLocale(), { timeZone: TZ })
}

/**
 * Returns a Date object representing today in the active timezone.
 * Use this instead of `new Date()` for calendar/week/month calculations.
 * @returns {Date}
 */
export function accountTodayDate() {
  const { year, month, day } = accountNow()
  return new Date(year, month, day, 12, 0, 0)
}

/** Returns current date/time components in the active timezone and locale. */
export function accountNow() {
  const parts = new Intl.DateTimeFormat(getLocale(), {
    timeZone: TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    weekday: 'short', hour: '2-digit', hour12: false,
  }).formatToParts(new Date())
  /** @param {string} type @returns {string} */
  const get = type => parts.find(p => p.type === type)?.value ?? ''
  const weekday = get('weekday')
  return {
    year: +get('year'),
    month: +get('month') - 1,
    day: +get('day'),
    dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      .findIndex(d => weekday.startsWith(d)),
  }
}

// ─── Deprecated aliases (kept for one release — use canonical names above) ─

/** @deprecated Use accountToday() instead */
export const todayVancouver = accountToday

/** @deprecated Use toAccountDate() instead */
export const toVancouverDate = toAccountDate

/** @deprecated Use accountTodayDate() instead */
export const vancouverToday = accountTodayDate

/** @deprecated Use accountNow() instead */
export const vancouverNow = accountNow

// ─── Formatted date/time helpers (active-tz aware) ─────────

/**
 * e.g. "Mon, Apr 11" — takes 'YYYY-MM-DD' string.
 * @param {string | null | undefined} d
 * @returns {string}
 */
export function formatDateShort(d) {
  if (!d) return ''
  return new Date(d + 'T12:00:00').toLocaleDateString(getLocale(), { timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric' })
}

/**
 * e.g. "Monday, April 11, 2026" — takes 'YYYY-MM-DD' string.
 * @param {string | null | undefined} d
 * @returns {string}
 */
export function formatDateFull(d) {
  if (!d) return ''
  return new Date(d + 'T12:00:00').toLocaleDateString(getLocale(), { timeZone: TZ, weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
}

/**
 * e.g. "April 2026" — takes a Date.
 * @param {Date} [date]
 * @returns {string}
 */
export function formatMonthYear(date = new Date()) {
  return date.toLocaleDateString(getLocale(), { timeZone: TZ, month: 'long', year: 'numeric' })
}

/**
 * e.g. "2:30 PM" — takes a timestamp string or Date.
 * @param {string | Date | null | undefined} ts
 * @returns {string}
 */
export function formatClockTime(ts) {
  if (!ts) return ''
  return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: TZ })
}
